import classNames from 'classnames';
import { isValid } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import SelectInput from './Select';

import styles from '../../../styles/fields/date-separated.module.scss';

const getDaysInMonth = (month: string, year: string) =>
    new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();

interface DateSeparatedProps {
    className?: string;
    withLabels?: boolean;
    value?: string;
    required?: boolean;
    onChange?: (value: string) => void;
}

const DateSeparated: React.FC<DateSeparatedProps> = ({
    className = null,
    required = false,
    withLabels = false,
    value = null,
    onChange = null,
}) => {
    const intl = useIntl();
    const { locale } = intl;
    const parsedValue = useMemo(() => (value !== null ? value.split('-') : null), [value]);
    const validParsedValue = useMemo(
        () => parsedValue !== null && isValid(new Date(parsedValue.join('-'))),
        [parsedValue],
    );
    const [day, setDay] = useState(parsedValue !== null ? parsedValue[2] : '');
    const [month, setMonth] = useState(parsedValue !== null ? parsedValue[1] : '');
    const [year, setYear] = useState(parsedValue !== null ? parsedValue[0] : '');

    const days = useMemo(
        () => (year !== '' && month !== '' ? getDaysInMonth(month, year) : 31),
        [month, year],
    );

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const months = useMemo(
        () =>
            [...Array(12)].map((_, i) =>
                withLabels
                    ? intl.formatDate(`2022-${`${i + 1}`.padStart(2, '0')}-01`, { month: 'long' })
                    : `${i + 1}`.padStart(2, '0'),
            ),
        [withLabels],
    );

    const updateDate = useCallback(
        (date: string, type: 'day' | 'month' | 'year') => {
            let finalDay = day;
            let finalMonth = month;
            let finalYear = year;
            if (type === 'day') {
                finalDay = date;
                setDay(date);
            } else if (type === 'month') {
                finalMonth = date;
                setMonth(date);
            } else if (type === 'year') {
                finalYear = date;
                setYear(date);
            }
            const validDate = finalDay !== '' && finalMonth !== '' && finalYear !== '';

            if (validDate) {
                const finalDays = getDaysInMonth(finalMonth, finalYear);
                if (finalDay !== '' && parseInt(finalDay, 10) > parseInt(finalDays, 10)) {
                    finalDay = `${finalDays}`;
                    setDay(finalDay);
                }

                if (onChange !== null) {
                    onChange(`${finalYear}-${finalMonth}-${finalDay}`);
                }
            }
        },
        [days, day, month, year, onChange],
    );

    const dayEl = () => (
        <SelectInput
            className={styles.item}
            value={validParsedValue ? parsedValue[2] : day}
            onChange={(newValue) => {
                updateDate(newValue, 'day');
            }}
            required={required}
            placeholder={
                withLabels
                    ? intl.formatMessage({
                          defaultMessage: 'Day',
                          description: 'Field placeholder',
                      })
                    : intl.formatMessage({ defaultMessage: 'DD', description: 'Field placeholder' })
            }
            items={[...Array(days)].map((_, dayIndex) => ({
                value: `${dayIndex + 1}`.padStart(2, '0'),
                label: `${dayIndex + 1}`.padStart(2, '0'),
            }))}
        />
    );

    const monthEl = () => (
        <SelectInput
            className={styles.item}
            value={validParsedValue ? parsedValue[1] : month}
            onChange={(newValue) => {
                updateDate(newValue, 'month');
            }}
            required={required}
            placeholder={
                withLabels
                    ? intl.formatMessage({
                          defaultMessage: 'Month',
                          description: 'Field placeholder',
                      })
                    : intl.formatMessage({ defaultMessage: 'MM', description: 'Field placeholder' })
            }
            items={months.map((monthLabel, monthIndex) => ({
                value: `${monthIndex + 1}`.padStart(2, '0'),
                label: monthLabel,
            }))}
        />
    );

    const yearEl = () => (
        <SelectInput
            className={styles.item}
            value={validParsedValue ? parsedValue[0] : year}
            onChange={(newValue) => {
                updateDate(newValue, 'year');
            }}
            required={required}
            placeholder={
                withLabels
                    ? intl.formatMessage({
                          defaultMessage: 'Year',
                          description: 'Field placeholder',
                      })
                    : intl.formatMessage({
                          defaultMessage: 'YYYY',
                          description: 'Field placeholder',
                      })
            }
            items={[...Array(currentYear - 1900)].map((_, yearIndex) => ({
                value: `${currentYear - yearIndex}`,
                label: `${currentYear - yearIndex}`,
            }))}
        />
    );

    return (
        <div className={classNames([styles.container, { [className]: className !== null }])}>
            {(locale === 'en' ? [monthEl, dayEl, yearEl] : [dayEl, monthEl, yearEl]).map(
                (El, elIndex) => (
                    <El key={`el-${elIndex + 1}`} className={styles.element} />
                ),
            )}
        </div>
    );
};

export default DateSeparated;
